import React from 'react';
import { useNotifications } from '../../contexts/NotificationContext';

/**
 * Global Modal Component
 * Hanterar success, error, info och confirm dialogs
 * Används automatiskt via NotificationContext
 */
const GlobalModal = () => {
  const { modal } = useNotifications();

  if (!modal) return null;

  const { type, title, message, onConfirm, onCancel, onClose, actions } = modal;

  const styles = {
    success: {
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      iconPath: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      buttonClass: 'bg-green-600 hover:bg-green-700'
    },
    error: {
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      iconPath: 'M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      buttonClass: 'bg-red-600 hover:bg-red-700'
    },
    info: {
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      iconPath: 'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z',
      buttonClass: 'bg-blue-600 hover:bg-blue-700'
    },
    confirm: {
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
      buttonClass: 'bg-leadme-pink hover:bg-leadme-pink-dark'
    }
  };

  const style = styles[type] || styles.info;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          onClick={type !== 'confirm' ? onClose : undefined}
        />

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        {/* Modal panel - Diskret design */}
        <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 animate-modal-in">
          <div className="sm:flex sm:items-start">
            {/* Icon - Mindre och till vänster */}
            <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 ${style.iconBg}`}>
              <svg className={`h-6 w-6 ${style.iconColor}`} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d={style.iconPath} />
              </svg>
            </div>

            {/* Content - Till höger om ikonen */}
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {title}
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500 whitespace-pre-line">
                  {message}
                </p>
              </div>
            </div>
          </div>

          {/* Buttons - Mindre och till höger */}
          <div className="mt-5 sm:mt-4 sm:flex sm:justify-end">
            {type === 'confirm' ? (
              <div className="flex flex-col-reverse sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={onCancel}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:w-auto sm:text-sm transition-all duration-200"
                >
                  Nej
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto sm:text-sm transition-all duration-200 hover:shadow-md active:scale-95 ${style.buttonClass}`}
                >
                  Ja
                </button>
              </div>
            ) : actions && actions.length > 0 ? (
              <div className="flex flex-col-reverse sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:w-auto sm:text-sm transition-all duration-200"
                >
                  Stäng
                </button>
                {actions.map((action, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => {
                      action.onClick();
                      if (!action.keepOpen) onClose();
                    }}
                    className={`w-full inline-flex items-center justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto sm:text-sm transition-all duration-200 hover:shadow-md active:scale-95 ${action.primary ? style.buttonClass : 'bg-indigo-600 hover:bg-indigo-700'}`}
                  >
                    {action.icon && (
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d={action.icon} />
                      </svg>
                    )}
                    {action.label}
                  </button>
                ))}
              </div>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto sm:text-sm transition-all duration-200 hover:shadow-md active:scale-95 ${style.buttonClass}`}
              >
                Stäng
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalModal;
